import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Clock, QrCode, Landmark, User, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "../context/AuthContext";
import { NotificationsBell } from "./NotificationsBell";

const navItems = [
  { to: "/", label: "Beranda", icon: Home, testid: "nav-beranda" },
  { to: "/riwayat", label: "Riwayat", icon: Clock, testid: "nav-riwayat" },
  { to: "/qris", label: "QRIS", icon: QrCode, center: true, testid: "nav-qris" },
  { to: "/rekening", label: "Rekening", icon: Landmark, testid: "nav-rekening" },
  { to: "/profil", label: "Profil", icon: User, testid: "nav-profil" },
];

export const BrandLogo = ({ className = "", light = false }) => (
  <span className={`font-logo font-bold text-2xl tracking-tight ${className}`}>
    <span className={light ? "text-white" : "text-[#151B4E]"}>Quantum</span>
    <span className="italic bg-gradient-to-r from-[#2E7CF6] to-[#2DD4BF] bg-clip-text text-transparent"> Pay</span>
  </span>
);

export const Layout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-dvh bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75">
        <div className="mx-auto w-full max-w-6xl px-4 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2">
            <BrandLogo />
            <span className="hidden sm:inline-block text-[10px] font-medium uppercase tracking-wider text-muted-foreground border rounded-full px-2 py-0.5">
              Sandbox
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {[
              { to: "/", label: "Beranda" },
              { to: "/transfer", label: "Transfer" },
              { to: "/top-up", label: "Top Up" },
              { to: "/qris", label: "QRIS" },
              { to: "/riwayat", label: "Riwayat" },
              { to: "/rekening", label: "Rekening" },
            ].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`desktop-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-[color,background-color] duration-200 ${
                    isActive ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1.5">
            <NotificationsBell />
            <Button
              size="sm"
              className="hidden md:inline-flex bg-gradient-to-r from-[#2E7CF6] to-[#1FC8A9] hover:opacity-90 text-white"
              onClick={() => navigate("/top-up")}
              data-testid="header-topup-button"
            >
              <PlusCircle className="h-4 w-4 mr-1" /> Top Up
            </Button>
            <button
              onClick={() => navigate("/profil")}
              data-testid="header-profile-button"
              className="h-9 w-9 rounded-full bg-primary/10 text-primary font-semibold text-sm flex items-center justify-center hover:bg-primary/20 transition-[background-color] duration-200"
              aria-label="Profil"
            >
              {(user?.name || "?").charAt(0).toUpperCase()}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto w-full max-w-6xl px-4 lg:px-8 py-6 pb-28 md:pb-10">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="grid grid-cols-5 h-16">
          {navItems.map((item) => {
            const Icon = item.icon;
            if (item.center) {
              return (
                <NavLink key={item.to} to={item.to} data-testid={item.testid} className="relative flex items-center justify-center">
                  <span className="absolute -top-5 h-14 w-14 rounded-full bg-gradient-to-r from-[#2E7CF6] to-[#1FC8A9] text-white flex items-center justify-center shadow-[0_8px_20px_rgba(46,124,246,0.35)] active:scale-95 transition-[box-shadow] duration-200">
                    <Icon className="h-6 w-6" />
                  </span>
                  <span className="absolute bottom-1.5 text-[10px] font-medium text-muted-foreground">{item.label}</span>
                </NavLink>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 text-[10px] font-medium transition-[color] duration-200 ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`
                }
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

// Backward-compatible alias (old imports)
export const FlipLogo = BrandLogo;
